/**
 * The wire types for the shell settings that follow a signed-in user: their
 * appearance choice (dark / light / auto) and the theme picked for each slot.
 *
 * Everything here is deliberately opaque. The values are the lunula's
 * own persistence blobs, produced and consumed by the toolkit's theme manager at
 * one end and stored as text at the other; neither Lunicle module parses one.
 * That is what keeps a toolkit upgrade that adds a field to a snapshot from
 * being a change to this file, to the database, or to the route between them.
 *
 * What is *not* opaque is which keys are allowed to travel — see [UiSettingKeys].
 *
 * @see se.soderbjorn.lunicle.clientserver.ApiRoutes.USER_UI_SETTINGS
 */
package se.soderbjorn.lunicle.clientserver

import kotlinx.serialization.Serializable

/**
 * The toolkit persistence keys Lunicle will store, spelled out.
 *
 * These are string copies of `se.soderbjorn.lunula.core.PersistKeys`
 * constants, and they are copies because this module has no business depending
 * on the toolkit: :clientServer is the wire, shared with the JVM server, which
 * has no browser shell in it at all. `ThemeKeysMatchToolkitTest` in :web — the
 * one module that does see both — asserts the copies still match, so a rename
 * upstream fails a test rather than silently stranding everyone's stored theme
 * under a key nothing reads any more.
 *
 * The set is an allowlist, enforced server-side. The alternative — storing
 * whatever key the client names — would turn a settings table into a per-user
 * scratch space that any signed-in browser could fill, and would make the
 * question "what does the server keep about me" unanswerable from the code.
 */
object UiSettingKeys {
    /**
     * `darkness.theme.v2.selection` — the appearance (Auto/Dark/Light) and the
     * theme chosen for each of the dark and light slots.
     *
     * This one key is the whole of what LNL-20 asked for; the next is here so
     * that what it names still exists.
     */
    const val THEME_SELECTION: String = "darkness.theme.v2.selection"

    /**
     * `darkness.theme.v2.custom` — themes the user built in the theme manager.
     *
     * Persisted together with the selection because the selection *names* one of
     * these by string. Storing the choice without the theme it points at would
     * be a reference to nothing: on the next load the toolkit would find an
     * unknown name and fall back to a built-in, so the user would have kept
     * their appearance and lost their colours.
     */
    const val THEME_CUSTOM: String = "darkness.theme.v2.custom"

    /**
     * `lunicle.userProjectPrefs.v1` — the caller's own view choices, per project.
     *
     * Unlike the two above, this key is **Lunicle's own, not the toolkit's**: it
     * does not pass through the lunula `Persister`, so it has no `PersistKeys`
     * counterpart and `ThemeKeysMatchToolkitTest` does not check it against the
     * toolkit — there is nothing upstream to match. The server still stores it
     * verbatim; the *client* is the one that parses it, into a
     * `UserProjectPrefs` per project id.
     *
     * One key holds every project's preferences as a single blob, keyed inside by
     * project id, and each project's record is a small open struct — today just
     * the columns that user has hidden (LNL-100). A future per-user-per-project
     * choice becomes a field on that record rather than another key here, another
     * entry in [persisted], and another round-trip on load: the whole of a user's
     * board preferences arrives and departs in one read and one write.
     *
     * Targeting the *id* of a status, not its name, is deliberate and is what the
     * issue asked for — a hidden column stays hidden across a rename, because the
     * rename leaves `StatusItem.id` untouched. See BoardState.StatusItem.
     */
    const val PROJECT_PREFS: String = "lunicle.userProjectPrefs.v1"

    /**
     * `lunicle.workspace.v1` — the caller's tabs and what is in them.
     *
     * Lunicle's own key, like [PROJECT_PREFS] and unlike the two theme ones: the
     * toolkit's [LAYOUT_STATE] below carries where the panes *sit*, but nothing
     * upstream knows what a Lunicle pane *is*. What round-trips here is exactly
     * that — the ordered tabs, and per pane its kind plus its argument, a board's
     * project or an issue's id. Small, and deliberately so: a tab is a working
     * set, and a working set is a list of names.
     *
     * One key for the whole workspace rather than one per tab, for
     * [PROJECT_PREFS]' reason: it arrives and departs in one read and one write,
     * and a tab reorder is then a single fact rather than a fan of them that
     * could half-apply.
     *
     * Signed out, nothing is stored and the default layout is seeded on every
     * load — see the client's workspace view model, which owns the format.
     */
    const val WORKSPACE: String = "lunicle.workspace.v1"

    /**
     * `darkness.layoutState` — where the panes sit inside each tab.
     *
     * A toolkit key, so it is a copy in the sense the two theme keys are and
     * `ThemeKeysMatchToolkitTest` checks it the same way. The blob is the
     * toolkit's and opaque here: per-tab layout preset, pane order, and each
     * pane's rectangle, z-order and maximised state.
     *
     * Stored because a working set that forgets its splits is not one. Lunicle
     * used to deliberately drop this — the board opened full-area on every load
     * and a persisted layout would have quietly undone that decision from another
     * file — but the board is no longer the one thing a tab can hold, and an
     * arrangement the user built by dragging is now the point rather than
     * something in the way of it.
     */
    const val LAYOUT_STATE: String = "darkness.layoutState"

    /**
     * `darkness.sidebarWidth` — how wide the user dragged the left sidebar.
     *
     * A toolkit key like [LAYOUT_STATE], and checked against its `PersistKeys`
     * original the same way. The blob is the toolkit's and opaque here: today
     * `{"leftPx": 210}`.
     *
     * Stored because the sidebar's width is now something an *app* has an
     * opinion about, not only the toolkit — Lunicle opens a tighter sidebar
     * when the tracker is embedded in another site's page (LNL-188), where the
     * chrome eats a column the host's slot cannot spare. That narrower value is
     * a seed for a browser that has never touched the handle; the only thing
     * that can outrank it is a width the user chose, and this key is where such
     * a width lives. Without it the embed's default would be re-imposed on
     * every load, on someone who had already said otherwise.
     *
     * Signed out, nothing is stored and the app's seed applies on every load —
     * which is the common case inside an embed.
     */
    const val SIDEBAR_WIDTH: String = "darkness.sidebarWidth"

    /**
     * `darkness.appearance.fonts` — which face and size the user picked for each
     * lettered surface: the sidebar and topbar, the tab strip, pane titles, prose,
     * headings, and monospaced content.
     *
     * A toolkit key like [LAYOUT_STATE] and [SIDEBAR_WIDTH], and opaque here: one
     * JSON object holding a family and a size per surface, any of them absent
     * meaning "this user picked nothing for it". The toolkit argues the one-blob
     * shape where it defines it; what it means here is that a font feature costs
     * this allowlist one entry rather than twelve.
     *
     * Stored because Lunicle *offers* the choice and then lost it. The Appearance
     * sidebar's font rows are unconditional chrome, every brand seam this app sets
     * is documented as yielding to "any font the user picks in Appearance" — and
     * until this key travelled, that pick lived in a JS object that died with the
     * tab. It clears the bar this list sets: not "different without it" but
     * *wrong* without it, because a control that visibly takes effect and has
     * forgotten by the next load is a broken control, and the deploy-time brand
     * font it yielded to came straight back over the top of it.
     *
     * Signed out, nothing is stored and the brand (then toolkit) default applies
     * on every load, as with every key here.
     */
    const val APPEARANCE_FONTS: String = "darkness.appearance.fonts"

    /**
     * `darkness.theme.v2.favorites` — the themes the user starred in the theme
     * manager.
     *
     * A toolkit key like [LAYOUT_STATE] and [APPEARANCE_FONTS], and opaque here: a
     * JSON array of theme names. Its own key upstream rather than a field on
     * [THEME_SELECTION], because a star answers "which of these do I want to find
     * again" while the selection answers "which one is on" — so starring has to
     * survive a theme change without being able to cause one.
     *
     * Stored for [APPEARANCE_FONTS]' reason and it clears the same bar: the ☆ is
     * drawn on every card of the theme manager this app mounts, so it is one click
     * away on the shell's own chrome, and until this key travelled that click
     * filled the star in and emptied it again on the next load. A control that
     * visibly takes effect and has forgotten by the next load is a broken control,
     * not a preference it would merely be nicer to keep.
     *
     * A star is also worth more here than the count of built-in themes suggests: a
     * branded deployment layers its own themes onto the user's own (see
     * ThemePersister), so the list this hoists from is as long as the instance
     * makes it.
     *
     * Signed out, nothing is stored and a visitor's stars live for the tab, as with
     * every key here.
     */
    const val THEME_FAVORITES: String = "darkness.theme.v2.favorites"

    /**
     * `darkness.appearance.shape` — how the user likes their windows: corner
     * radius, spacing density, surfaces (depth or flat) and selection (tinted or
     * filled).
     *
     * A toolkit key like [APPEARANCE_FONTS], and opaque here: the toolkit's
     * `AppearanceShape.toJson()`, one JSON object with any field absent meaning
     * "this user picked nothing for it". One object for the same reason the fonts
     * are one: a shape setting the toolkit grows later (the shell style and the
     * navigation mode are on their way) becomes a field in this blob rather than
     * another entry here.
     *
     * This was the deliberate holdout when the fonts and the stars started
     * travelling (88ead88, 1bfeed4): the same class of preference, kept off the
     * list as a standing call about how much of a signed-in browser this app
     * stores, not because anything about it was harder. That call changed (LNA-26):
     * a shape the Appearance sidebar offers unconditionally is a choice the user
     * made, and it should be the same choice on the next machine.
     *
     * Only the user's own picks reach it. A deployment's brand.json shape is a
     * fallback the toolkit paints *beneath* the stored value and never writes into
     * it, so a branded instance stores nothing on anyone's behalf — and a re-brand
     * still reaches everyone who never stated a preference. ThemePersister has the
     * one wrinkle, which is a shell that outlives a change of identity.
     *
     * Signed out, nothing is stored and the shape lives for the tab, as with every
     * key here.
     */
    const val APPEARANCE_SHAPE: String = "darkness.appearance.shape"

    /** Every key the server will store. Anything else is refused. */
    val persisted: Set<String> = setOf(
        THEME_SELECTION,
        THEME_CUSTOM,
        PROJECT_PREFS,
        WORKSPACE,
        LAYOUT_STATE,
        SIDEBAR_WIDTH,
        APPEARANCE_FONTS,
        THEME_FAVORITES,
        APPEARANCE_SHAPE,
    )
}

/**
 * Everything the server holds for the caller, and who the caller is.
 *
 * @property userId the effective user these settings belong to, or null when
 *   nobody is signed in. Carried rather than inferred because the client uses it
 *   to answer "are these still the right settings?" without a second request:
 *   the shell is mounted once and the signed-in user can change underneath it —
 *   sign-in, sign-out, an admin starting or stopping impersonation — and this is
 *   the field that says the loaded settings now belong to somebody else. See
 *   ThemePersister.
 * @property settings key to blob, holding only the keys in
 *   [UiSettingKeys.persisted]. Absent keys are absent, not empty strings: "never
 *   chosen" has to stay distinguishable from "chosen, and the value is blank",
 *   because the first means "use the default" and the second does not.
 */
@Serializable
data class UiSettingsState(
    val userId: Long? = null,
    val settings: Map<String, String> = emptyMap(),
)

/**
 * Store one setting.
 *
 * One key per request rather than the whole map, because that is how the toolkit
 * writes: a theme change is two independent `Persister.write` calls, and
 * batching them here would mean this side buffering writes it does not own and
 * guessing when the batch is complete.
 *
 * @property key must be one of [UiSettingKeys.persisted]; anything else is a 400.
 * @property value the toolkit's blob, stored verbatim.
 */
@Serializable
data class SetUiSettingRequest(
    val key: String,
    val value: String,
)
